import _ from 'lodash';
import BaseAccountController from '../../lib/controllers/BaseAccountController';

declare var LtFramework: lt.Static;

export default class DetailsController extends BaseAccountController {
	public static $inject: string[] = ['$scope', '$q', 'Profile', 'ClubVisitResource', 'MembershipResource', 'UserResource', 'UserPermissionsResource'];

	public membership: MyAccountApi.IMembership;
	public numClubVisitsThisMonth: number;
	public numGuestPassesRemaining: number;
	public user: MyAccountApi.IUser;
	public userPermissions: MyAccountApi.IUserPermissions;
	public q: ng.IQService;

	private readonly ClubVisit: MyAccountApi.IClubVisitResource;
	private readonly MembershipResource: MyAccountApi.IMembershipResource;
	private readonly User: MyAccountApi.IUserResource;
	private readonly UserPermissionsResource: MyAccountApi.IUserPermissionsResource;

	private authenticated: boolean;

	constructor($scope: ng.IScope, q: ng.IQService, Profile: lt.IProfile, ClubVisit: MyAccountApi.IClubVisitResource,
			MembershipResource: MyAccountApi.IMembershipResource, User: MyAccountApi.IUserResource, UserPermissionsResource: MyAccountApi.IUserPermissionsResource) {
		super($scope, Profile);

		this.ClubVisit = ClubVisit;
		this.MembershipResource = MembershipResource;
		this.User = User;
		this.UserPermissionsResource = UserPermissionsResource;
		this.q = q;

		// see if the user is authenticated (has an SSO ID)
		const profileStore = LtFramework.getStore('ProfileStore') as lt.IProfileStore;
		this.authenticated = !!profileStore.getInitialData().sso;
	}

	/**
	 * Return true if there's no data currently waiting to be populated.
	 * That's the case if the user is not authenticated (no data to populate),
	 * or if the user is authenticated and all the service calls have returned.
	 */
	public isLoading(): boolean {
		if (!this.authenticated) {
			return false;
		}

		return !(this.user && this.membership && this.userPermissions);
	}

	/**
	 * Returns true if the user is logged in
	 */
	public isAuthenticated(): boolean {
		return this.authenticated;
	}

	public isMember(): boolean {
		return ((this.profile.memberState === 'REMEMBERED' || this.profile.memberState === 'AUTHENTICATED') && !!this.profile.memberDetails);
	}

	public isAccessMember(): boolean {
		return !!(this.profile && this.profile.memberDetails && this.profile.memberDetails.membershipLevelGroupName.toLowerCase() !== 'non-access');
	}

	public isDayPassOwner(): boolean {
		return !!(this.profile && this.profile.memberDetails && this.profile.memberDetails.resourceKeys
		&& (_.filter(this.profile.memberDetails.resourceKeys, ['category', 'dayPass']).length > 0));
	}

	public isLTHealthD2C(): boolean {
		return !!(this.profile && this.profile.memberDetails && this.profile.memberDetails.resourceKeys
		&& (_.filter(this.profile.memberDetails.resourceKeys, ['displayName', 'B2B Life Time Total Health D2C']).length > 0));
	}

	public getMemberLevel(): string {
		return !this.isMember() ? '' : this.profile.memberDetails!.membershipLevel.toLowerCase();
	}

	public getSsoId(): string {
		return (LtFramework.getStore('ProfileStore') as lt.IProfileStore).getInitialData().sso;
	}

	/**
	 * Triggered whenever SSO ID is available/changed
	 */
	protected loadData(): void {
		this.authenticated = true;

		// load My Account user details
		const promises = {
			user: this.User.get().$promise,
			userPermissions: this.UserPermissionsResource.get().$promise,
			membership: this.MembershipResource.get().$promise
		};
		// call services and assign results, then bind applicable urls with partner logic
		this.q.all(promises).then( (responses) => {
			this.user = responses.user;
			this.userPermissions = responses.userPermissions;
			this.membership = responses.membership;
			LtFramework.actions.bindPartnerUrls();
		});

		// Load club visits
		// Per MYACCT-118, all visits in the same day count as a single club visit
		this.ClubVisit.query({ month_offset: 0}, (clubVisits: MyAccountApi.IClubVisit[]) => {
			this.numClubVisitsThisMonth = _.uniqBy(clubVisits, (visit: MyAccountApi.IClubVisit) => {
				return visit.usageDate;
			}).length;
		});
	}

	/**
	 * Triggered whenever profile data is available/changed
	 */
	protected profileDataLoaded(): void {
		return;
	}
}
